import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from '../../../../../components/icons'
import Text from '../../../../../elements/Typo/Text'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import { useUserSelector } from '../../../../../store/selectors/userSelector'
import { ErrorResponse, SuccessApiResponse, SuccessResponse } from '../../../../../store/api/apiTypes'
import DataTableModal from '../modals/dataTable.modal'
import Dropdown from '../../../../../elements/Form/dropdown'
import { useFpLoader } from '../../../../../store/api/hooks/fpLoaderHook'
import { SelectedDataTableType } from '../../../../../_config/types'
import { useExperimentSelector } from '../../../../../store/selectors/experimentSelector'
import { useDialog } from '../../../../../store/api/hooks/dialogHook'
import { Tooltip } from 'react-daisyui'
import { trackEvent } from '../../../../../_common/functions'
import { useMeasurementConfigure } from '../../../../../_common/hook/useMeasurementConfigure'
import toast from 'react-hot-toast'
import { IoWarning } from 'react-icons/io5'
import RecurringFileUploadErrorModal from '../../../../../components/DataFileUploader/recurringFileUploadError.modal'

interface Props {
    _ref?: any
    _for: 'experiment' | 'measurement' | 'intervention' | 'recommendation'
    extType?: 'MTE' | 'RCT' | ''
    selectedDataTable: (data: SelectedDataTableType | null) => void
    experimentData?: any
    recurringSelection?: boolean
}

const DataSelection: React.FC<Props> = ({ _ref, _for, extType = '', selectedDataTable, experimentData = null, recurringSelection = false }) => {
    const { t } = useTranslation()
    const { showLoader, hideLoader } = useFpLoader()
    const selectedExperiment = useExperimentSelector()
    const experiment = experimentData ?? selectedExperiment
    const dialog = useDialog()
    const user: any = useUserSelector()
    const experimentApi = useExperimentApi()

    const [zones, setZones] = useState<any[] | null>(null)
    const [zone, setZone] = useState<any>(null)
    const [selectedTable, setSelectedTable] = useState<SelectedDataTableType | null>(null)
    const [userDesignData, setUserDesignData] = useState<any[] | null>(null)
    const [zoneDesignData, setzoneDesignData] = useState<any[] | null>(null)
    const [measurementDesignData, setMeasurementDesignData] = useState<any[] | null>(null)
    const measurementHookFunction = useMeasurementConfigure({ experimentType: 'RCT' })

    const recurringFileUploadErrorModalRef: any = useRef()
    const [recurringFileUploadError, setRecurringFileUploadError] = useState<any>(null)

    const dataTableModalRef: any = useRef()

    const getUserDesignData = () => {
        experimentApi.getuserdesign(
            {},
            (data: SuccessApiResponse<{ result: [] }>) => {
                setUserDesignData(data.data.result.filter((item: any) => !!item.name))
            },
            (error: ErrorResponse<[]>) => {
                console.log(error)
            },
        )
    }
    const sortedZones = user?.zones
        ?.map((zone: any) => ({
            ...zone,
            label: zone.title,
            value: zone.zone_literal,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label)) // sort by title
    const getZoneDesignData = (selectedZone: any = null) => {
        const zonedata = selectedZone ? selectedZone.value : sortedZones?.[0]?.zone_literal
        if (zonedata) {
            showLoader()
            experimentApi.getzonedesign(
                {
                    zone: zonedata, //'zone_001',
                },
                (data: SuccessResponse<[]>) => {
                    setzoneDesignData(data.data.filter((item: any) => !!item.name))
                    hideLoader()
                },
                (error: ErrorResponse<[]>) => {
                    console.log(error)
                    hideLoader()
                },
            )
        }
    }

    const getMeasurementDesignData = (selectedZone: any = null) => {
        const zonedata = selectedZone ? selectedZone.value : sortedZones?.[0]?.zone_literal
        showLoader()
        experimentApi.getzonedesign(
            {
                zone: zonedata, //'zone_001',
                table_type: 'KPI',
            },
            (resp: any) => {
                setMeasurementDesignData(resp.data.filter((item: any) => !!item.name))
                hideLoader()
            },
            (error: ErrorResponse<[]>) => {
                console.log(error)
                hideLoader()
            },
        )
    }

    const getInterventionDesignData = (selectedZone: any = null) => {
        const zonedata = selectedZone ? selectedZone.value : sortedZones?.[0]?.zone_literal
        showLoader()
        experimentApi.getzonedesign(
            {
                zone: zonedata, //'zone_001',
                table_type: 'Intervention',
            },
            (resp: any) => {
                setMeasurementDesignData(resp.data.filter((item: any) => !!item.name))
                hideLoader()
            },
            (error: ErrorResponse<[]>) => {
                console.log(error)
                hideLoader()
            },
        )
    }

    const getRecommendationDesignData = (selectedZone: any = null) => {
        const zonedata = selectedZone ? selectedZone.value : sortedZones?.[0]?.zone_literal
        showLoader()
        experimentApi.getzonedesign(
            {
                zone: zonedata, //'zone_001',
                table_type: 'Recommendation',
            },
            (resp: any) => {
                setMeasurementDesignData(resp.data.filter((item: any) => !!item.name))
                hideLoader()
            },
            (error: ErrorResponse<[]>) => {
                console.log(error)
                hideLoader()
            },
        )
    }

    const openMeasurementTableModal = (data: any) => {
        setSelectedTable({
            type: 'measurement',
            table: data.value,
        })
        dataTableModalRef?.current.showModal()
    }

    const selectMeasurementTableForRecurring = (data: any) => {
        experimentApi.getRecurringRequiredColumnList(
            {
                literal: experiment.experiment_literal,
                delta_measurement_table_name: data.value.name,
            },
            () => openMeasurementTableModal(data),
            (error: any) => {
                setRecurringFileUploadError(error.error)
            },
        )
    }

    const selectMeasurementTable = recurringSelection ? selectMeasurementTableForRecurring : openMeasurementTableModal

    const openDesignTableModal = (data: any) => {
        trackEvent('wizard_design_table_selected', { table_name: data?.value?.name })
        setSelectedTable({
            type: 'design',
            table: data.value,
        })
        dataTableModalRef?.current.showModal()
    }

    const selectDesignTableForRecurring = (data: any) => {
        trackEvent('wizard_design_table_selected', { table_name: data?.value?.name })
        experimentApi.getRecurringRequiredColumnList(
            {
                literal: experiment.experiment_literal,
                delta_measurement_table_name: data.value.name,
            },
            () => {
                setSelectedTable({
                    type: 'design',
                    table: data.value,
                })
                dataTableModalRef?.current.showModal()
            },
            (error: any) => {
                toast.custom(
                    (t) => (
                        <div
                            className={`${t.visible ? 'animate-custom-enter' : 'animate-custom-leave'} 
                max-w-md w-full bg-yellow-200 shadow-lg p-3 rounded-lg pointer-events-auto flex ring-1 ring-yellow-600 ring-opacity-5`}
                        >
                            <div className="text-[0.667rem] font-sans">
                                <div className="text-[0.667rem]">
                                    <div className="flex gap-3">
                                        <p className="font-normal text-gray-900 leading-[1.3]">
                                            <IoWarning className="h-4 w-4 text-yellow-700 shrink-0 inline" /> {error?.message}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center pt-2">
                                    <button type="button" onClick={() => toast.dismiss(t.id)} className=" bg-black text-white p-2 rounded-sm text-[0.667rem] leading-[1.3] font-sans">
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    ),
                    { duration: 15000 },
                )
            },
        )
    }

    const selectDesignTable = recurringSelection ? selectDesignTableForRecurring : openDesignTableModal

    const openUserTableModal = (data: any) => {
        trackEvent('wizard_user_table_selected', { table_name: data?.value?.table_name })
        setSelectedTable({
            type: 'user',
            table: { ...data.value, name: data.value.table_name, alias: data.value.table_alias, userTable: true },
        })
        dataTableModalRef?.current.showModal()
    }

    const selectUserTableForRecurring = (data: any) => {
        trackEvent('wizard_user_table_selected', { table_name: data?.value?.table_name })
        experimentApi.getRecurringRequiredColumnList(
            {
                literal: experiment.experiment_literal,
                delta_measurement_table_name: data.value.table_name,
            },
            () => {
                setSelectedTable({
                    type: 'design',
                    table: data.value,
                })
                dataTableModalRef?.current.showModal()
            },
            (error: any) => {
                toast.error(error?.message)
            },
        )
    }

    const selectUserTable = recurringSelection ? selectUserTableForRecurring : openUserTableModal

    const _handleOnChangeZone = (e: any) => {
        setZone(e)
        trackEvent('wizard_zone_selected', { zone: e?.label, for: _for })
        if (_for == 'measurement') {
            getMeasurementDesignData(e)
        } else if (_for == 'intervention') {
            getInterventionDesignData(e)
        } else if (_for == 'recommendation') {
            getRecommendationDesignData(e)
        } else {
            getZoneDesignData(e)
        }
    }

    const onContinue = () => {
        selectedDataTable(selectedTable)
        trackEvent('data_table_selected', {
            for: _for,
            type: selectedTable?.type,
            table_name: selectedTable?.table?.name,
            table_alias: selectedTable?.table?.alias,
        })

        dataTableModalRef?.current?.close()
    }

    const onCancel = () => {
        selectedDataTable(null)
        setSelectedTable(null)
        dataTableModalRef?.current?.close()
    }

    const updateWithExperimentSelectedTable = () => {
        if (experiment?.user_design?.table_name) {
            const file: SelectedDataTableType = {
                type: 'user',
                table: {
                    name: experiment?.user_design?.table_name,
                    alias: '',
                },
            }
            setSelectedTable(file)
            selectedDataTable(file)
        }

        if (experiment.data_mappings?.delta_design_table_name) {
            const file: SelectedDataTableType = {
                type: 'design',
                table: {
                    name: experiment.data_mappings?.delta_design_table_name,
                    alias: '',
                },
            }
            setSelectedTable(file)
            selectedDataTable(file)
        }
    }

    const updateWithMeasurementSelectedTable = () => {
        if (experiment.data_mappings?.delta_measurement_table_name) {
            const file: SelectedDataTableType = {
                type: 'design',
                table: {
                    name: experiment.data_mappings?.delta_measurement_table_name,
                    alias: '',
                },
            }
            setSelectedTable(file)
            selectedDataTable(file)
        }
    }

    const updateWithInterventionSelectedTable = () => {
        if (experiment.data_mappings?.delta_intervention_table_name) {
            const file: SelectedDataTableType = {
                type: 'design',
                table: {
                    name: experiment.data_mappings?.delta_intervention_table_name,
                    alias: '',
                },
            }
            setSelectedTable(file)
            selectedDataTable(file)
        }
    }

    const updateWithRecommendationSelectedTable = () => {
        if (experiment.data_mappings?.delta_recommendation_table_name) {
            const file: SelectedDataTableType = {
                type: 'design',
                table: {
                    name: experiment.data_mappings?.delta_recommendation_table_name,
                    alias: '',
                },
            }
            setSelectedTable(file)
            selectedDataTable(file)
        }
    }

    const customOption = (props: any) => {
        const { data, isSelected, innerRef, innerProps } = props
        return (
            <div ref={innerRef} {...innerProps} className={`${isSelected ? 'bg-primary' : 'white'} hover:bg-primary hover:bg-opacity-50 gap-2 px-2 py-1 text-[0.583rem] cursor-pointer flex items-center justify-between`}>
                <span className="truncate">{data.label}</span>
                <span className="whitespace-nowrap bg-[#f4f4f4] rounded-[0.333rem] !text-[0.542rem] px-1 leading-1">{data.value.alias ?? data.value.table_alias}</span>
            </div>
        )
    }

    useEffect(() => {
        if (user) {
            setZones(
                user?.zones
                    ?.map((zone: any) => ({
                        ...zone,
                        label: zone.title,
                        value: zone.zone_literal,
                    }))
                    .sort((a: any, b: any) => a.label.localeCompare(b.label)),
            )
        }
    }, [user])

    useEffect(() => {
        if (zones) {
            setZone(zones[0])
        }
    }, [zones])

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.reset = onCancel
        }
    }, [_ref])

    useEffect(() => {
        if (experiment && !experimentData && !recurringSelection) {
            if (_for == 'experiment') {
                updateWithExperimentSelectedTable()
            } else if (_for == 'measurement') {
                updateWithMeasurementSelectedTable()
            } else if (_for == 'intervention') {
                updateWithInterventionSelectedTable()
            } else if (_for == 'recommendation') {
                updateWithRecommendationSelectedTable()
            } else {
                setSelectedTable(null)
                selectedDataTable(null)
            }
        }
    }, [experiment])

    const onDesignTableReset = () => {
        if (experiment.step > 1 && (experiment?.data_mappings?.delta_design_table_name || experiment?.user_design?.table_name) && !experimentData) {
            dialog.confirm({
                type: 'delete',
                title: t('Change Experiment'),
                message: t('Are you sure you want to change your Experiment details?'),
                warning: t('By Changing this, it will alter the existing experiment details'),
                confirmButtonLabel: t('Yes Proceed'),
                onConfirm: () => {
                    trackEvent('wizard_change_table_confirmed', {})
                    onCancel()
                },
                onCancel: () => {
                    trackEvent('wizard_change_table_cancelled', {})
                    console.log('No')
                },
            })
        } else {
            onCancel()
        }
    }

    const onMeasurementTableReset = () => {
        if (recurringSelection) {
            onCancel()
            return
        }
        if (experiment?.data_mappings?.delta_measurement_table_name || experiment?.data_mappings?.user_created_measurement_table_name) {
            if (experiment.step > 5 && !experiment?.is_draft) {
                measurementHookFunction?.deleteInsightsWarning(() => {
                    onCancel()
                })
            } else {
                dialog.confirm({
                    type: 'delete',
                    title: t('Change Experiment'),
                    message: t('Are you sure you want to change your Experiment details?'),
                    warning: t('By Changing this, it will alter the existing experiment details'),
                    confirmButtonLabel: t('Yes Proceed'),
                    onConfirm: () => {
                        onCancel()
                    },
                    onCancel: () => {
                        console.log('No')
                    },
                })
            }
        } else {
            onCancel()
        }
    }

    const changeTableSelection = () => {
        if (_for == 'experiment') {
            onDesignTableReset()
        } else if (_for == 'measurement') {
            onMeasurementTableReset()
        } else {
            onCancel()
        }
    }
    const sortedZoneDesignData = zoneDesignData ? [...zoneDesignData].sort((a: any, b: any) => a.name.localeCompare(b.name)) : []
    const sortedUserDesignData = userDesignData ? [...userDesignData].sort((a: any, b: any) => a.table_name.localeCompare(b.table_name)) : []
    const sortedMeasurementDesignData = measurementDesignData ? [...measurementDesignData].sort((a: any, b: any) => a.name.localeCompare(b.name)) : []

    const [tooltipMessage, setTooltipMessage] = useState('')

    useEffect(() => {
        if (_for === 'experiment') {
            setTooltipMessage(t('Select the data for design from any existing data source or a table pre uploaded to the tool'))
        } else if (_for === 'measurement' && extType === 'MTE') {
            setTooltipMessage(t('Select the table that contains the KPI outcomes you want to measure.'))
        } else if (_for === 'measurement') {
            setTooltipMessage(t('Select the data for measurement from any existing data source or a table pre uploaded to the tool'))
        } else if (_for === 'intervention') {
            setTooltipMessage(t('Select the table that contains treatment or intervention details applied to each unit.'))
        }
    }, [_for, extType, t])

    return (
        <div ref={_ref}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1">
                    <p className="text-[#414040] mb-0 leading-6">{t('Select Data from existing tables')}</p>
                    <Tooltip message={tooltipMessage} className="max-w-[5rem]" color="info" position="top">
                        <Icon name="outlineInfoCircle" size="0.6" className="mt-[2px]" />
                    </Tooltip>
                </div>

                <Dropdown value={zone} onChange={_handleOnChangeZone} options={zones || []} />
            </div>

            <div className="p-4  h-[18.167rem] flex-grow rounded-[0.417rem] border border-dashed border-[##BEBEBE]">
                <div className="flex flex-col items-center justify-center h-full">
                    {selectedTable == null ? (
                        <>
                            {userDesignData == null && zoneDesignData == null && measurementDesignData == null && (
                                <Text size="sm" className="mb-4 text-[#00000080] max-w-[19.667rem]">
                                    {t('Please Select the most relevant data table from the available options. This data must contain the most granular information about the sample on which you intend to run your tests. The more the better!')}
                                </Text>
                            )}

                            {userDesignData == null && zoneDesignData == null && measurementDesignData == null ? (
                                <button
                                    type="button"
                                    className="flex text-[0.667rem] items-center justify-between border border-[#d4d4d4] rounded-[0.167rem] max-w-[20.708rem] w-full h-[2.583rem] p-2"
                                    onClick={() => {
                                        trackEvent('wizard_select_table_btn_clicked', {})
                                        if (_for == 'experiment') {
                                            if (experiment.step > 1 && experiment?.data_mappings?.user_created_table_name) {
                                                dialog.confirm({
                                                    type: 'delete',
                                                    title: t('Change Experiment'),
                                                    message: t('Are you sure you want to change your Experiment details?'),
                                                    warning: t('By Changing this, it will alter the existing experiment details'),
                                                    confirmButtonLabel: t('Yes Proceed'),
                                                    onConfirm: () => {
                                                        getZoneDesignData()
                                                        getUserDesignData()
                                                    },
                                                    onCancel: () => {
                                                        console.log('No')
                                                    },
                                                })
                                            } else {
                                                getZoneDesignData()
                                                getUserDesignData()
                                            }
                                        } else if (_for == 'intervention') {
                                            getInterventionDesignData()
                                        } else if (_for == 'measurement') {
                                            if (experiment?.data_mappings?.user_created_measurement_table_name && !recurringSelection) {
                                                dialog.confirm({
                                                    type: 'delete',
                                                    title: t('Change Measurement'),
                                                    message: t('Are you sure you want to change your Measurement details?'),
                                                    warning: t('By Changing this, it will alter the existing Measurement details'),
                                                    confirmButtonLabel: t('Yes Proceed'),
                                                    onConfirm: () => {
                                                        getMeasurementDesignData()
                                                    },
                                                    onCancel: () => {
                                                        console.log('No')
                                                    },
                                                })
                                            } else {
                                                getMeasurementDesignData()
                                            }
                                        } else if (_for == 'recommendation') {
                                            if (experiment?.data_mappings?.user_created_recommendation_table_name) {
                                                dialog.confirm({
                                                    type: 'delete',
                                                    title: t('Change Measurement'),
                                                    message: t('Are you sure you want to change your Measurement details?'),
                                                    warning: t('By Changing this, it will alter the existing Measurement details'),
                                                    confirmButtonLabel: t('Yes Proceed'),
                                                    onConfirm: () => {
                                                        getRecommendationDesignData()
                                                    },
                                                    onCancel: () => {
                                                        console.log('No')
                                                    },
                                                })
                                            } else {
                                                getRecommendationDesignData()
                                            }
                                        }
                                    }}
                                >
                                    <span>{t('Select Table')}</span>
                                    <Icon name="arrow-right" size={0.667} />
                                </button>
                            ) : (
                                <div className="max-w-[20.5rem] w-full ">
                                    {_for == 'experiment' ? (
                                        <>
                                            <p className="my-1 text-[13px] italic text-slate-500">{t('Choose a data table from any one')}</p>
                                            <Dropdown
                                                className="mb-1"
                                                classNamePrefix="testops-lg"
                                                placeholder={t('Select Design Table')}
                                                onChange={selectDesignTable}
                                                options={sortedZoneDesignData?.map((data: any) => ({
                                                    label: data.name,
                                                    value: data,
                                                }))}
                                                components={{ Option: customOption }}
                                            />
                                            <Dropdown
                                                className="mb-1"
                                                classNamePrefix="testops-lg"
                                                placeholder={t('Select User Table')}
                                                onChange={selectUserTable}
                                                options={sortedUserDesignData.map((data: any) => ({
                                                    label: data.table_name,
                                                    value: data,
                                                }))}
                                                components={{ Option: customOption }}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Dropdown
                                                className="mb-1"
                                                classNamePrefix="testops-lg"
                                                placeholder={t('Select User Table')}
                                                onChange={selectMeasurementTable}
                                                options={sortedMeasurementDesignData.map((data: any) => ({
                                                    label: data.name,
                                                    value: data,
                                                }))}
                                                components={{ Option: customOption }}
                                            />
                                            {/* <div className="cursor-pointer mt-1 flex text-[0.667rem] items-center justify-between border border-[#d4d4d4] rounded-[0.167rem] w-[20.708rem] h-[2.583rem] p-2" onClick={toggleExpanded1}>
                                                <span>{t('Measurement Table')}</span>
                                                {expanded ? <Icon name="arrow-right" size={0.667} /> : <Icon name="arrow-right" size={0.667} />}
                                            </div>

                                            <div className={`rounded-[0.167rem] overflow-auto  w-[20.708rem] transition-[max-height] duration-300 ease-in ${expanded1 ? 'max-h-[8.375rem] mt-1 border border-[#D4D4DA]' : 'max-h-0 mt-0'}`}>
                                                {measurementDesignData?.map((userTable: any, index: number) => (
                                                    <div key={index} className="h-10 p-2 flex items-center justify-between border-b border-[#e4e4e4]">
                                                        <div className="flex items-center justify-between text-black w-full">
                                                            <Text
                                                                size="md"
                                                                className="cursor-pointer truncate"
                                                                onClick={() => {
                                                                    selectMeasurementTable(userTable)
                                                                }}
                                                                title={userTable.name}
                                                            >
                                                                {userTable.name}
                                                            </Text>

                                                            <Text className="bg-[#f4f4f4] rounded-[0.333rem] !text-[0.542rem] whitespace-nowrap px-1">{extractTableName(userTable.alias)}</Text>
                                                        </div>
                                                        <div></div>
                                                    </div>
                                                ))}
                                            </div> */}
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="w-full">
                            <div className="flex text-[0.667rem] items-center justify-between border border-[#d4d4d4] rounded-[0.167rem] w-full max-w-[20.708rem] h-[2.583rem] p-2">
                                <div className="flex gap-2 items-center flex-grow">
                                    <span className="truncate flex-grow w-[1rem]">{selectedTable?.table?.name}</span>
                                    <Text className="bg-[#f4f4f4] whitespace-nowrap rounded-[0.333rem] !text-[0.542rem] px-1">{selectedTable?.table?.alias}</Text>
                                </div>
                                <Icon name="arrow-right" size={0.667} />
                            </div>
                            <div className="text-end">
                                <Text size="14" className="cursor-pointer underline" onClick={changeTableSelection}>
                                    {t('Change')}
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <DataTableModal _ref={dataTableModalRef} literal={experiment?.experiment_literal ?? ''} table={selectedTable} onContinue={onContinue} onCancel={onCancel} />

            <RecurringFileUploadErrorModal _ref={recurringFileUploadErrorModalRef} error={recurringFileUploadError} setRecurringFileUploadError={setRecurringFileUploadError} />
        </div>
    )
}

export default DataSelection
